import React, { useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';

interface MultiSelectFieldProps {
  field: any;
  form: any;
  options: { value: string; label: string }[];
  placeholder?: string;
  width?: string | number;
  onChangeCallback?: (_selectedValues: string[]) => void;
  disabled?: boolean;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  field,
  form,
  options,
  placeholder = 'Select',
  width = '300px',
  onChangeCallback,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selectedValues: string[] = Array.isArray(field.value) ? field.value : [];
  const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const newValue = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    form.setFieldTouched(field.name, true, true);
    form.setFieldValue(field.name, newValue, true);
    if (onChangeCallback) {
      onChangeCallback(newValue as string[]);
    }
    setOpen(false);
  };

  const handleChipDelete = (valueToDelete: string) => () => {
    const newSelectedValues = selectedValues.filter(id => id !== valueToDelete);
    form.setFieldValue(field.name, newSelectedValues, true);
    form.setFieldTouched(field.name, true, true);
  };

  const selectedItems = options?.filter((option: any) =>
    selectedValues.includes(option.value)
  );

  return (
    <FormControl error={hasError} sx={{ width }} disabled={disabled}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Select
          multiple
          disabled={disabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedValues}
          onChange={handleSelectChange}
          input={<OutlinedInput />}
          displayEmpty
          renderValue={(selected) =>
            selected.length === 0 ? (
              <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{placeholder}</span>
            ) : (
              ''
            )
          }
          sx={{ width }}
        >
          <MenuItem value="" disabled>
            <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>-- Select --</span>
          </MenuItem>
          {options?.map((option: any) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        {selectedItems?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {selectedItems.map((item: any) => (
              <Chip
                key={item.value}
                label={item.label}
                onDelete={handleChipDelete(item.value)}
              />
            ))}
          </Box>
        )}
      </Box>
    </FormControl>
  );
};
